/**
 * Classify Mitek authenticity for admin Identity UX.
 *
 * Hard fails (high-confidence Fraudulent / FAIL / facial mismatch) stay Failed.
 * Low-confidence fraud, Undetermined, Caution, and similar vendor signals are
 * Review — Identity may still complete; Overview + LRA should pick them up.
 *
 * Probability is Mitek’s 0–1000 scale (same threshold as IV FACE_COMPARISON).
 */

import type { IdentityLookupRow, MitekVerificationCheck } from '../api/identityVerificationDashboardApi';

export const MITEK_HARD_FAIL_PROBABILITY_MIN = 700;

export type MitekAuthenticitySignal = 'none' | 'pass' | 'review' | 'hard_fail';

const PASS_MARKERS = ['AUTHENTIC', 'PASS', 'PASSED', 'OK', 'SUCCESS', 'CLEAR'] as const;
const HARD_FAIL_MARKERS = [
  'FRAUDULENT',
  'FRAUD',
  'FAILED',
  'FAIL',
  'REJECTED',
  'REJECT',
  'DENIED',
  'NO MATCH',
] as const;
const REVIEW_MARKERS = [
  'UNDETERMINED',
  'CAUTION',
  'INCONCLUSIVE',
  'REVIEW',
  'MANUAL',
  'PENDING',
] as const;

function upper(value: unknown): string {
  return String(value ?? '').trim().toUpperCase();
}

function isPassJudgement(judgement: string): boolean {
  const u = upper(judgement);
  return PASS_MARKERS.some((m) => u === m || u.includes(m));
}

function isHardFailJudgement(judgement: string): boolean {
  const u = upper(judgement);
  if (!u || isPassJudgement(u)) return false;
  return HARD_FAIL_MARKERS.some((m) => u === m || u.includes(m));
}

function isReviewJudgement(judgement: string): boolean {
  const u = upper(judgement);
  if (!u || isPassJudgement(u)) return false;
  if (isHardFailJudgement(u)) return false;
  return REVIEW_MARKERS.some((m) => u === m || u.includes(m));
}

function rowProbability(row: {
  probability?: number | string | null;
  [k: string]: unknown;
}): number | null {
  const p = row.probability;
  if (typeof p === 'number' && Number.isFinite(p)) return p;
  if (typeof p === 'string' && p.trim()) {
    const n = Number(p);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** One Mitek check: high-confidence fraud/fail vs low-confidence / undetermined. */
export function classifyMitekCheckJudgement(
  judgement: unknown,
  probability: number | null | undefined,
): 'pass' | 'review' | 'hard_fail' | 'other' {
  const j = String(judgement ?? '').trim();
  if (!j) return 'other';
  if (isPassJudgement(j)) return 'pass';
  const prob = typeof probability === 'number' && Number.isFinite(probability) ? probability : null;
  if (isHardFailJudgement(j)) {
    if (prob !== null && prob < MITEK_HARD_FAIL_PROBABILITY_MIN) return 'review';
    return 'hard_fail';
  }
  if (isReviewJudgement(j)) return 'review';
  return 'other';
}

function checksResultVerifications(row: IdentityLookupRow): Array<{
  judgement: string;
  probability: number | null;
  name?: string;
}> {
  const checks = row.checks_result;
  if (!checks || typeof checks !== 'object' || Array.isArray(checks)) return [];
  const raw = (checks as { verifications?: unknown }).verifications;
  if (!Array.isArray(raw)) return [];
  const out: Array<{ judgement: string; probability: number | null; name?: string }> = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const rec = item as Record<string, unknown>;
    const judgement = String(rec.judgement ?? rec.status ?? '').trim();
    if (!judgement) continue;
    const name = String(rec.display_label ?? rec.name ?? rec.check_key ?? '').trim();
    out.push({ judgement, probability: rowProbability(rec), name: name || undefined });
  }
  return out;
}

function dossierVerifications(row: IdentityLookupRow): MitekVerificationCheck[] {
  return Array.isArray(row.dossier?.verifications) ? row.dossier!.verifications : [];
}

function allVerificationRows(row: IdentityLookupRow): Array<{
  judgement: string;
  probability: number | null;
  name?: string;
}> {
  const fromDossier = dossierVerifications(row).map((v) => ({
    judgement: v.judgement,
    probability: v.probability,
    name: v.name,
  }));
  if (fromDossier.length > 0) return fromDossier;
  return checksResultVerifications(row);
}

export function mitekAuthenticityReason(row: IdentityLookupRow): string | null {
  const primary = row.evidenceProcessingPrimary?.primaryMessage?.trim();
  if (primary) return primary;
  const failed = allVerificationRows(row).find((v) => {
    const c = classifyMitekCheckJudgement(v.judgement, v.probability);
    return c === 'hard_fail' || c === 'review';
  });
  if (failed?.name) {
    const kind =
      classifyMitekCheckJudgement(failed.judgement, failed.probability) === 'review'
        ? 'needs review'
        : 'failed';
    return `Document check ${kind}: ${failed.name} (${failed.judgement}).`;
  }
  return null;
}

export type MitekAuthenticityOperatorCopy = {
  title: string;
  body: string;
  action: string;
};

function primaryIssuePlain(message: string): string | null {
  const m = message.trim();
  if (!m) return null;
  if (/quality too low|image quality/i.test(m)) {
    return 'The submitted ID photo was not clear enough for a confident authenticity check.';
  }
  if (/authenticity checks? failed/i.test(m)) {
    return 'The submitted ID did not pass automated authenticity checks.';
  }
  if (/authenticity uncertain|could not confirm|not confirm.*authentic/i.test(m)) {
    return 'The automated document check could not confirm this ID is authentic with enough confidence.';
  }
  if (/expir/i.test(m)) return null;
  return null;
}

function verificationIssuePlain(
  name: string,
  _judgement: string,
  signal: 'review' | 'hard_fail',
): string {
  const n = name.toLowerCase();
  if (n.includes('barcode')) {
    return signal === 'review'
      ? 'The barcode on the ID could not be verified with enough confidence.'
      : 'The barcode on the ID did not pass verification.';
  }
  if (n.includes('liveness')) {
    return signal === 'review'
      ? 'A document liveness check raised a low-confidence concern — this is not a definite forgery finding.'
      : 'The document did not pass a liveness check (it may be a copy or screenshot).';
  }
  if (n.includes('face')) {
    return 'The selfie did not strongly match the photo on the ID.';
  }
  if (signal === 'review') {
    return 'One or more automated document checks could not confirm the ID is authentic with enough confidence.';
  }
  return 'One or more automated document checks indicate the ID may not be authentic.';
}

function plainMitekIssue(
  row: IdentityLookupRow,
  signal: 'review' | 'hard_fail',
): string {
  const primary = row.evidenceProcessingPrimary?.primaryMessage?.trim();
  if (primary) {
    const fromPrimary = primaryIssuePlain(primary);
    if (fromPrimary) return fromPrimary;
  }

  const flagged = allVerificationRows(row).find((v) => {
    const c = classifyMitekCheckJudgement(v.judgement, v.probability);
    return signal === 'review' ? c === 'review' : c === 'hard_fail';
  });
  if (flagged?.name) {
    return verificationIssuePlain(flagged.name, flagged.judgement, signal);
  }

  if (signal === 'review') {
    return 'The automated document check could not confirm this ID is authentic with enough confidence.';
  }
  return 'The submitted ID did not pass automated authenticity checks.';
}

/**
 * Plain-language Identity copy for operators — what went wrong, why review is needed,
 * and what to do next. Technical vendor labels stay in the Mitek panel below.
 */
export function mitekAuthenticityOperatorCopy(
  row: IdentityLookupRow,
  signal: 'review' | 'hard_fail',
): MitekAuthenticityOperatorCopy {
  const issue = plainMitekIssue(row, signal);

  if (signal === 'review') {
    return {
      title: 'ID document needs manual review',
      body: `${issue} Identity completed, but loan review must confirm the ID photos before approving.`,
      action:
        'Open the verification attempts and Mitek sections below, compare the ID images, then approve, request a new upload, or decline.',
    };
  }

  return {
    title: 'Failed — document could not be verified',
    body: `${issue} The customer cannot continue on this ID until they submit a valid government-issued document.`,
    action:
      'Ask the customer to submit a clearer photo of a valid government ID, or decline if you suspect fraud.',
  };
}

/**
 * Overall authenticity signal for a lookup row.
 * `none` when Mitek never ran.
 */
export function classifyMitekAuthenticity(row: IdentityLookupRow): MitekAuthenticitySignal {
  if (!row.mitekCalled) return 'none';

  const rows = allVerificationRows(row);
  const classes = rows.map((v) => classifyMitekCheckJudgement(v.judgement, v.probability));
  if (classes.includes('hard_fail')) return 'hard_fail';
  if (classes.includes('review')) return 'review';

  const auth = row.dossier?.findings?.authenticated;
  if (auth === false) return 'review';

  const status = String(row.session_status ?? '').toUpperCase();
  if (status === 'FAILED' || status === 'REJECTED') {
    const code = String(row.evidenceProcessingPrimary?.primaryCode ?? '').toUpperCase();
    if (/FRAUD|FORGERY|FACE.?MISMATCH|NO.?MATCH/.test(code)) return 'hard_fail';
    if (code.includes('FAIL') || code.includes('REJECT') || code.includes('ERROR')) {
      return 'review';
    }
  }

  if (auth === true) return 'pass';
  if (rows.length > 0 && classes.every((c) => c === 'pass' || c === 'other')) return 'pass';
  return 'review';
}
